import type { RuleGroupType } from 'react-querybuilder';

import type { GuardrailRuleInfo } from '~/server_shared/openapi/data-contracts';

import { flattenLeaves, hasAiConditionReference } from '../../../AutomaticRulesConfiguration/Guardrails/aiConditions';

import type { GuardrailRequiredCondition, ScopeRow } from './types';

/**
 * Whether the rule's scope intersects the scope the action is being configured for.
 *
 * An intersection, not an equality: an all-sub-orgs / all-LOBs rule genuinely does govern this
 * action even though it was authored elsewhere, so it must be listed. A rule scoped to a
 * *different* sub-org or LOB does not apply here and listing it would tell the admin the agent is
 * gated on a scope where it is not. Mirrors the filter the Guardrails screen applies
 * (`AutomaticRulesConfiguration/Guardrails/index.tsx`): an empty list on a dimension means the
 * rule was not narrowed on it.
 */
const scopeIntersects = (rule: GuardrailRuleInfo, scope: ScopeRow): boolean => {
  const ruleScope = rule.scope;
  if (!ruleScope) {
    return true;
  }
  const subOrgIds = ruleScope.sub_organization_ids ?? [];
  // A flat organization has no sub-organizations at all, so no rule can be narrowed away from it.
  const subOrgMatches =
    ruleScope.is_all_sub_orgs ||
    scope.sub_organization_id === null ||
    subOrgIds.length === 0 ||
    subOrgIds.includes(scope.sub_organization_id);

  const ruleLobs = ruleScope.lobs ?? [];
  const lobMatches = ruleScope.is_all_lobs || ruleLobs.length === 0 || ruleLobs.includes(scope.lob);

  return subOrgMatches && lobMatches;
};

/**
 * Whether a guardrail rule is one of this claim action's.
 *
 * Three conditions, and all are load-bearing:
 *
 * 1. **The trigger event is one the action reports.** A guardrail hangs off a CMS domain event,
 *    and the action reports one or more (an inspection reports a different event for an exposure
 *    and for the whole claim) — so a rule on any other event cannot gate this action.
 * 2. **The rule references the AI context in a way an agent's call can satisfy.** The same event
 *    is reported when an *adjuster* performs the action manually, so the event alone does not
 *    separate the agent's guardrails from the human ones. Such a rule can only match an agent call
 *    (`build_context` binds `is_ai_agent: false` with no confidence leaf for a person), which is
 *    exactly what "this action's guardrail" means here. Rules authored from this screen always
 *    carry `ai.is_ai_agent is true` for the same reason. Reading `ai.*` is not by itself enough:
 *    `ai.is_ai_agent` tested for *false* is the human's rule on the same event, and listing it
 *    here would put it beneath a banner promising the agent cannot override it — see
 *    `hasAiConditionReference`.
 * 3. **The rule's scope intersects the action's scope** — see `scopeIntersects`.
 * 4. **The rule carries every condition the action requires.** Close and reopen report the same
 *    event, and only `New Status` tells one's guardrails from the other's.
 */
export const isClaimActionGuardrail = (
  rule: GuardrailRuleInfo,
  guardrailEventKeys: string[] | null | undefined,
  scope: ScopeRow,
  requiredConditions?: GuardrailRequiredCondition[] | null
): boolean => {
  if (!guardrailEventKeys?.includes(rule.trigger_event)) {
    return false;
  }
  if (!scopeIntersects(rule, scope)) {
    return false;
  }
  const conditions = rule.conditions as RuleGroupType | null;
  const leaves = flattenLeaves(conditions);
  const hasRequiredConditions = (requiredConditions ?? []).every((required) =>
    leaves.some(
      (leaf) =>
        leaf.field === required.field && leaf.operator === required.operator && String(leaf.value) === required.value
    )
  );
  return hasRequiredConditions && hasAiConditionReference(conditions);
};
